import { entities, enums, formatters } from "@fraction/shared";
import _ from "lodash";
import { ModifiableKeyValue } from "src/components/ModifiableKeyValue";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";

export function ApplicationValuationKeyValue({
  appId,
  label,
  tooltip,
  type,
  loading,
}: {
  appId: string | undefined;
  label: string;
  tooltip: string;
  type: enums.PropertyValuationType;
  loading?: boolean;
}) {
  const { preference } = useAccountViewPreference();

  return (
    <ModifiableKeyValue
      appId={appId}
      loading={loading}
      label={label}
      tooltip={tooltip}
      path="property.valuations"
      formatValue={(val: entities.PropertyValuation[]) => {
        return (
          formatters.number
            .getCurrencyFromNumber(
              _.sortBy(val, (x) => x?.date || x?.createdAt)
                .reverse()
                .find((x) => x.type === type)?.value || ""
            )
            ?.toString() || ""
        );
      }}
      cacheUpdateFnProperty={(prev: entities.Property | undefined | null, changes: entities.Property) => {
        // we only sent in one
        const changedValuation = changes?.valuations?.[0];

        if (!changedValuation) {
          return prev;
        }

        const existingAppraisal = _.sortBy(prev?.valuations, (x) => x?.date || x?.createdAt)
          .reverse()
          .find((x) => x.type === type && x.source === changedValuation?.source);

        if (existingAppraisal) {
          return Object.assign(prev || {}, {
            valuations: prev?.valuations?.map((x: entities.PropertyValuation) => {
              if (x.id === existingAppraisal.id) {
                return {
                  ...x,
                  ...changedValuation,
                };
              }

              return x;
            }),
          });
        }

        return Object.assign(prev || {}, {
          valuations: [...(prev?.valuations || []), changedValuation],
        });
      }}
      mutateValueBeforeUpdate={(val, prev) => {
        return [
          {
            date: new Date(),
            value: formatters.number.getCents(val),
            type: type,
            source:
              preference === "employee"
                ? enums.PropertyValuationSource.INTERNAL
                : ["applicant", "broker"].includes(preference)
                ? enums.PropertyValuationSource.APPLICANT
                : enums.PropertyValuationSource.INTERNAL,
          },
        ];
      }}
      type="currency"
    />
  );
}
